import XLSX from 'xlsx-js-style';
import { DateColumn, MatrixCell, MatrixRow, ReportData, ReportViewType } from '../types/reports';
import {
  aggregateCells,
  AggregatedCell,
  AggregatedColumn,
  aggregateColumnsMonthly,
  aggregateColumnsWeekly,
  formatTimeForReport,
} from './reportHelpers';

// Professional color palette
const colors = {
  primary: '2C3E50', // Dark slate blue - headers
  primaryLight: '34495E', // Lighter slate - total column header
  accent: '5D6D7E', // Medium gray-blue
  border: 'BDC3C7', // Light gray - borders
  borderDark: '7F8C8D', // Darker gray - accent borders
  weekend: 'F8F9FA', // Very light gray - weekend background
  totalColumn: 'EBF5FB', // Very light blue - total column
  grandTotal: 'D5DBDB', // Light warm gray - grand total row
  groupDepth0: 'E8EEF2', // Light blue-gray - top-level group
  groupDepth1: 'F0F4F7', // Very light blue-gray - nested group
  white: 'FFFFFF',
  black: '2C3E50',
};

// Style definitions
const styles = {
  header: {
    font: { bold: true, color: { rgb: colors.white } },
    fill: { fgColor: { rgb: colors.primary } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: {
      top: { style: 'thin', color: { rgb: colors.primary } },
      bottom: { style: 'thin', color: { rgb: colors.primary } },
      left: { style: 'thin', color: { rgb: colors.primary } },
      right: { style: 'thin', color: { rgb: colors.primary } },
    },
  },
  headerName: {
    font: { bold: true, color: { rgb: colors.white } },
    fill: { fgColor: { rgb: colors.primary } },
    alignment: { horizontal: 'left', vertical: 'center' },
    border: {
      top: { style: 'thin', color: { rgb: colors.primary } },
      bottom: { style: 'thin', color: { rgb: colors.primary } },
      left: { style: 'thin', color: { rgb: colors.primary } },
      right: { style: 'thin', color: { rgb: colors.primary } },
    },
  },
  cell: {
    alignment: { horizontal: 'center', vertical: 'center' },
    border: {
      top: { style: 'thin', color: { rgb: colors.border } },
      bottom: { style: 'thin', color: { rgb: colors.border } },
      left: { style: 'thin', color: { rgb: colors.border } },
      right: { style: 'thin', color: { rgb: colors.border } },
    },
  },
  cellName: {
    alignment: { horizontal: 'left', vertical: 'center' },
    border: {
      top: { style: 'thin', color: { rgb: colors.border } },
      bottom: { style: 'thin', color: { rgb: colors.border } },
      left: { style: 'thin', color: { rgb: colors.border } },
      right: { style: 'thin', color: { rgb: colors.border } },
    },
  },
  weekend: {
    alignment: { horizontal: 'center', vertical: 'center' },
    fill: { fgColor: { rgb: colors.weekend } },
    border: {
      top: { style: 'thin', color: { rgb: colors.border } },
      bottom: { style: 'thin', color: { rgb: colors.border } },
      left: { style: 'thin', color: { rgb: colors.border } },
      right: { style: 'thin', color: { rgb: colors.border } },
    },
  },
  totalRow: {
    font: { bold: true },
    alignment: { horizontal: 'center', vertical: 'center' },
    fill: { fgColor: { rgb: colors.grandTotal } },
    border: {
      top: { style: 'thin', color: { rgb: colors.borderDark } },
      bottom: { style: 'thin', color: { rgb: colors.borderDark } },
      left: { style: 'thin', color: { rgb: colors.borderDark } },
      right: { style: 'thin', color: { rgb: colors.borderDark } },
    },
  },
  totalRowName: {
    font: { bold: true },
    alignment: { horizontal: 'left', vertical: 'center' },
    fill: { fgColor: { rgb: colors.grandTotal } },
    border: {
      top: { style: 'thin', color: { rgb: colors.borderDark } },
      bottom: { style: 'thin', color: { rgb: colors.borderDark } },
      left: { style: 'thin', color: { rgb: colors.borderDark } },
      right: { style: 'thin', color: { rgb: colors.borderDark } },
    },
  },
  totalColumn: {
    font: { bold: true },
    alignment: { horizontal: 'center', vertical: 'center' },
    fill: { fgColor: { rgb: colors.totalColumn } },
    border: {
      top: { style: 'thin', color: { rgb: colors.border } },
      bottom: { style: 'thin', color: { rgb: colors.border } },
      left: { style: 'thin', color: { rgb: colors.borderDark } },
      right: { style: 'thin', color: { rgb: colors.borderDark } },
    },
  },
  totalColumnHeader: {
    font: { bold: true, color: { rgb: colors.white } },
    fill: { fgColor: { rgb: colors.primaryLight } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: {
      top: { style: 'thin', color: { rgb: colors.primary } },
      bottom: { style: 'thin', color: { rgb: colors.primary } },
      left: { style: 'thin', color: { rgb: colors.primary } },
      right: { style: 'thin', color: { rgb: colors.primary } },
    },
  },
  grandTotalCell: {
    font: { bold: true },
    alignment: { horizontal: 'center', vertical: 'center' },
    fill: { fgColor: { rgb: colors.grandTotal } },
    border: {
      top: { style: 'medium', color: { rgb: colors.borderDark } },
      bottom: { style: 'medium', color: { rgb: colors.borderDark } },
      left: { style: 'thin', color: { rgb: colors.borderDark } },
      right: { style: 'thin', color: { rgb: colors.borderDark } },
    },
  },
};

/**
 * Returns group row style with a subtle background color based on depth
 */
const getGroupStyle = (depth: number, base: Record<string, unknown>): Record<string, unknown> => {
  const bgColor = depth === 0 ? colors.groupDepth0 : colors.groupDepth1;
  return {
    ...base,
    font: { ...((base.font as Record<string, unknown>) || {}), bold: true },
    fill: { fgColor: { rgb: bgColor } },
  };
};

/**
 * Formats seconds to Excel time format (H:MM)
 */
const formatTime = (seconds: number): string => {
  if (seconds === 0) return '';
  return formatTimeForReport(seconds);
};

/**
 * Flattens hierarchical rows for Excel export
 */
const flattenRowsForExport = (rows: MatrixRow[], indent: number = 0): MatrixRow[] => {
  const result: MatrixRow[] = [];

  for (const row of rows) {
    result.push({ ...row, depth: indent });
    if (row.children) {
      result.push(...flattenRowsForExport(row.children, indent + 1));
    }
  }

  return result;
};

/**
 * Generates indent prefix based on depth
 */
const getIndent = (depth: number): string => {
  return '  '.repeat(depth);
};

/**
 * Formats row label as "Name (KEY)" when entityKey is available
 */
const formatRowLabel = (row: MatrixRow): string => {
  const cleanLabel = row.entityKey && row.label.startsWith(`${row.entityKey} - `)
    ? row.label.slice(row.entityKey.length + 3)
    : row.label;
  const label = row.entityKey ? `${cleanLabel} (${row.entityKey})` : row.label;
  return `${getIndent(row.depth)}${label}`;
};

/**
 * Converts column index to Excel column letter (0 = A, 1 = B, etc.)
 */
const getColumnLetter = (colIndex: number): string => {
  let letter = '';
  let temp = colIndex;
  while (temp >= 0) {
    letter = String.fromCharCode((temp % 26) + 65) + letter;
    temp = Math.floor(temp / 26) - 1;
  }
  return letter;
};

/**
 * Gets aggregated columns based on view type
 */
const getAggregatedColumnsForExport = (
  columnDates: DateColumn[],
  viewType: ReportViewType
): AggregatedColumn[] | null => {
  switch (viewType) {
    case 'weekly':
      return aggregateColumnsWeekly(columnDates);
    case 'monthly':
      return aggregateColumnsMonthly(columnDates);
    default:
      return null;
  }
};

/**
 * Creates header label for aggregated column
 */
const getAggregatedColumnLabel = (col: AggregatedColumn): string => {
  return col.subLabel ? `${col.label} ${col.subLabel}` : col.label;
};

/**
 * Gets column width based on view type
 */
const getColumnWidthForView = (viewType: ReportViewType): number => {
  switch (viewType) {
    case 'weekly':
      return 12;
    case 'monthly':
      return 14;
    default:
      return 10;
  }
};

interface ExportOptions {
  viewType?: ReportViewType;
  filename?: string;
}

/**
 * Exports report data to Excel file with formatting
 */
export const exportReportToExcel = (reportData: ReportData, options: ExportOptions = {}): void => {
  const { viewType = 'daily', filename } = options;
  const { rows, columnDates, grandTotal, dateRange } = reportData;

  // Flatten rows
  const flatRows = flattenRowsForExport(rows);

  // Get aggregated columns for weekly/monthly views
  const aggregatedColumns = getAggregatedColumnsForExport(columnDates, viewType);
  const columnCount = aggregatedColumns ? aggregatedColumns.length : columnDates.length;
  const totalRowIndex = flatRows.length + 1; // 0-indexed, +1 for header
  const colWidth = getColumnWidthForView(viewType);

  // Build header row with styles
  const headers = buildHeaders(columnDates, aggregatedColumns, viewType);

  // Build data rows with styles
  const dataRows = flatRows.map((row) => {
    return buildDataRow(row, columnDates, aggregatedColumns, viewType);
  });

  // Build grand total row with styles
  const grandTotalRow = buildGrandTotalRow(grandTotal.dailyTotals, grandTotal.totalSeconds, aggregatedColumns);

  // Combine all rows
  const worksheetData = [headers, ...dataRows, grandTotalRow];

  // Create worksheet
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

  // Set column widths
  const colWidths = [
    { wch: 50 }, // Name column
    { wch: 20 }, // Labels column
    { wch: 10 }, // First Total column
    ...Array(columnCount).fill({ wch: colWidth }), // Date/aggregated columns
    { wch: 10 }, // Last Total column
  ];
  worksheet['!cols'] = colWidths;

  // Set row heights
  const rowHeights: XLSX.RowInfo[] = [];
  for (let i = 0; i <= totalRowIndex; i++) {
    rowHeights.push({ hpt: 20 }); // 20 points height
  }
  worksheet['!rows'] = rowHeights;

  // Freeze first row and first column
  worksheet['!freeze'] = { xSplit: 1, ySplit: 1 };

  // Create workbook
  const workbook = XLSX.utils.book_new();

  // Sheet name based on view type
  const sheetName = viewType === 'daily' ? 'Daily' : viewType === 'weekly' ? 'Weekly' : 'Monthly';
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  // Generate filename with view type suffix
  const viewSuffix = viewType !== 'daily' ? `_${viewType}` : '';
  const defaultFilename = `report_${dateRange.startDate}_${dateRange.endDate}${viewSuffix}.xlsx`;
  const finalFilename = filename || defaultFilename;

  // Download file
  XLSX.writeFile(workbook, finalFilename);
};

/**
 * Build headers row based on view type
 */
const buildHeaders = (
  columnDates: DateColumn[],
  aggregatedColumns: AggregatedColumn[] | null,
  viewType: ReportViewType
): Array<{ v: string; s: typeof styles.header }> => {
  const headers = [
    { v: 'Name', s: styles.headerName },
    { v: 'Labels', s: styles.headerName },
    { v: 'Total', s: styles.totalColumnHeader },
  ];

  if (aggregatedColumns) {
    // Weekly/Monthly view
    headers.push(
      ...aggregatedColumns.map((col) => ({
        v: getAggregatedColumnLabel(col),
        s: styles.header,
      }))
    );
  } else {
    // Daily view
    headers.push(
      ...columnDates.map((col: DateColumn) => ({
        v: `${col.dayName} ${col.dayNumber}`,
        s: styles.header,
      }))
    );
  }

  headers.push({ v: 'Total', s: styles.totalColumnHeader });
  return headers;
};

/**
 * Build data row based on view type
 */
const buildDataRow = (
  row: MatrixRow,
  columnDates: DateColumn[],
  aggregatedColumns: AggregatedColumn[] | null,
  viewType: ReportViewType
// eslint-disable-next-line @typescript-eslint/no-explicit-any
): Array<{ v: string; s: any }> => {
  const isGroup = row.children && row.children.length > 0;

  const cellNameStyle = isGroup ? getGroupStyle(row.depth, styles.cellName) : styles.cellName;
  const totalColStyle = isGroup ? getGroupStyle(row.depth, styles.totalColumn) : styles.totalColumn;
  const cellStyle = isGroup ? getGroupStyle(row.depth, styles.cell) : styles.cell;

  const cells = [
    { v: formatRowLabel(row), s: cellNameStyle },
    { v: row.labels ? row.labels.join(', ') : '', s: cellNameStyle },
    { v: formatTime(row.totalSeconds), s: totalColStyle },
  ];

  if (aggregatedColumns) {
    // Aggregate cells for weekly/monthly view
    const aggregatedCells = aggregateCells(row.cells, aggregatedColumns);
    cells.push(
      ...aggregatedCells.map((cell: AggregatedCell) => ({
        v: formatTime(cell.totalSeconds),
        s: cellStyle,
      }))
    );
  } else {
    // Daily view
    cells.push(
      ...row.cells.map((cell, idx) => ({
        v: formatTime(cell.totalSeconds),
        s: isGroup
          ? getGroupStyle(row.depth, columnDates[idx].isWeekend ? styles.weekend : styles.cell)
          : (columnDates[idx].isWeekend ? styles.weekend : styles.cell),
      }))
    );
  }

  cells.push({ v: formatTime(row.totalSeconds), s: totalColStyle });
  return cells;
};

/**
 * Build grand total row based on view type
 */
const buildGrandTotalRow = (
  dailyTotals: MatrixCell[],
  totalSeconds: number,
  aggregatedColumns: AggregatedColumn[] | null
): Array<{ v: string; s: typeof styles.grandTotalCell }> => {
  const cells = [
    { v: 'Grand Total', s: styles.totalRowName },
    { v: '', s: styles.grandTotalCell },
    { v: formatTime(totalSeconds), s: styles.grandTotalCell },
  ];

  if (aggregatedColumns) {
    // Aggregate cells for weekly/monthly view
    const aggregatedCells = aggregateCells(dailyTotals, aggregatedColumns);
    cells.push(
      ...aggregatedCells.map((cell: AggregatedCell) => ({
        v: formatTime(cell.totalSeconds),
        s: styles.grandTotalCell,
      }))
    );
  } else {
    // Daily view
    cells.push(
      ...dailyTotals.map((cell) => ({
        v: formatTime(cell.totalSeconds),
        s: styles.grandTotalCell,
      }))
    );
  }

  cells.push({ v: formatTime(totalSeconds), s: styles.grandTotalCell });
  return cells;
};

/**
 * Exports report data to Excel with more detailed formatting (two sheets)
 */
export const exportReportToExcelDetailed = (reportData: ReportData, filename?: string): void => {
  const { rows, columnDates, grandTotal, dateRange } = reportData;

  // Flatten rows
  const flatRows = flattenRowsForExport(rows);

  // Create workbook
  const workbook = XLSX.utils.book_new();

  // === Sheet 1: Summary (hours as numbers) ===
  const summaryHeaders = [
    { v: 'Name', s: styles.headerName },
    { v: 'Labels', s: styles.headerName },
    { v: 'Level', s: styles.header },
    ...columnDates.map((col: DateColumn) => ({
      v: col.date,
      s: styles.header,
    })),
    { v: 'Total (h)', s: styles.totalColumnHeader },
  ];

  const summaryData = flatRows.map((row) => {
    const isGroup = row.children && row.children.length > 0;
    return [
      { v: formatRowLabel(row), s: isGroup ? getGroupStyle(row.depth, styles.cellName) : styles.cellName },
      { v: row.labels ? row.labels.join(', ') : '', s: isGroup ? getGroupStyle(row.depth, styles.cellName) : styles.cellName },
      { v: row.level, s: isGroup ? getGroupStyle(row.depth, styles.cell) : styles.cell },
      ...row.cells.map((cell, idx) => ({
        v: cell.totalSeconds > 0 ? Math.round((cell.totalSeconds / 3600) * 100) / 100 : '',
        s: isGroup
          ? getGroupStyle(row.depth, columnDates[idx].isWeekend ? styles.weekend : styles.cell)
          : (columnDates[idx].isWeekend ? styles.weekend : styles.cell),
      })),
      { v: Math.round((row.totalSeconds / 3600) * 100) / 100, s: isGroup ? getGroupStyle(row.depth, styles.totalColumn) : styles.totalColumn },
    ];
  });

  const summaryTotalRow = [
    { v: 'Grand Total', s: styles.totalRowName },
    { v: '', s: styles.grandTotalCell },
    { v: '', s: styles.grandTotalCell },
    ...grandTotal.dailyTotals.map((cell) => ({
      v: cell.totalSeconds > 0 ? Math.round((cell.totalSeconds / 3600) * 100) / 100 : '',
      s: styles.grandTotalCell,
    })),
    { v: Math.round((grandTotal.totalSeconds / 3600) * 100) / 100, s: styles.grandTotalCell },
  ];

  const summarySheet = XLSX.utils.aoa_to_sheet([summaryHeaders, ...summaryData, summaryTotalRow]);

  summarySheet['!cols'] = [
    { wch: 50 },
    { wch: 20 },
    { wch: 10 },
    ...columnDates.map(() => ({ wch: 12 })),
    { wch: 12 },
  ];

  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

  // === Sheet 2: Formatted (H:MM) ===
  const formattedHeaders = [
    { v: 'Name', s: styles.headerName },
    { v: 'Labels', s: styles.headerName },
    { v: 'Total', s: styles.totalColumnHeader },
    ...columnDates.map((col: DateColumn) => ({
      v: `${col.dayName} ${col.dayNumber}`,
      s: styles.header,
    })),
    { v: 'Total', s: styles.totalColumnHeader },
  ];

  const formattedData = flatRows.map((row) => {
    const isGroup = row.children && row.children.length > 0;
    return [
      { v: formatRowLabel(row), s: isGroup ? getGroupStyle(row.depth, styles.cellName) : styles.cellName },
      { v: row.labels ? row.labels.join(', ') : '', s: isGroup ? getGroupStyle(row.depth, styles.cellName) : styles.cellName },
      { v: formatTime(row.totalSeconds), s: isGroup ? getGroupStyle(row.depth, styles.totalColumn) : styles.totalColumn },
      ...row.cells.map((cell, idx) => ({
        v: formatTime(cell.totalSeconds),
        s: isGroup
          ? getGroupStyle(row.depth, columnDates[idx].isWeekend ? styles.weekend : styles.cell)
          : (columnDates[idx].isWeekend ? styles.weekend : styles.cell),
      })),
      { v: formatTime(row.totalSeconds), s: isGroup ? getGroupStyle(row.depth, styles.totalColumn) : styles.totalColumn },
    ];
  });

  const formattedTotalRow = [
    { v: 'Grand Total', s: styles.totalRowName },
    { v: '', s: styles.grandTotalCell },
    { v: formatTime(grandTotal.totalSeconds), s: styles.grandTotalCell },
    ...grandTotal.dailyTotals.map((cell) => ({
      v: formatTime(cell.totalSeconds),
      s: styles.grandTotalCell,
    })),
    { v: formatTime(grandTotal.totalSeconds), s: styles.grandTotalCell },
  ];

  const formattedSheet = XLSX.utils.aoa_to_sheet([formattedHeaders, ...formattedData, formattedTotalRow]);

  formattedSheet['!cols'] = [
    { wch: 50 },
    { wch: 20 },
    { wch: 10 },
    ...columnDates.map(() => ({ wch: 10 })),
    { wch: 10 },
  ];

  XLSX.utils.book_append_sheet(workbook, formattedSheet, 'Formatted');

  // Generate filename
  const defaultFilename = `report_${dateRange.startDate}_${dateRange.endDate}.xlsx`;
  const finalFilename = filename || defaultFilename;

  // Download file
  XLSX.writeFile(workbook, finalFilename);
};
